import * as Sentry from '@sentry/react';
import { lazy, Suspense, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { useAuth } from './lib/auth/useAuth';
import { AuthProvider } from './lib/auth/AuthProvider';
import { AccountProvider } from './lib/account/AccountProvider';
import { SubscriptionProvider } from './lib/subscription/SubscriptionProvider';
import { RouteGuard } from './components/auth/RouteGuard';
import { Layout } from './routes/_layout';
import { WizardShell } from './routes/_wizard-shell';
import { useToast } from './components/ui/Toast';
import { useReloadOnNavigationWhenStale } from './hooks/useReloadOnNavigationWhenStale';
import { ToastProvider } from './components/ui/ToastProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { appVersion, isRequestValidationError, staleBundleToast } from './lib/app-version';
import { shouldReportToSentry, toErrorToast } from './lib/error-handling';
import { ApiError } from '@packages/api';
import { LoadingState } from './components/ui/LoadingState';
import {
  loadLogin,
  loadSignup,
  loadForgotPassword,
  loadResetPassword,
  loadVerifyEmail,
  loadVerifyEmailPending,
  loadUnsubscribe,
  loadOnboarding,
  loadActivate,
  loadSelectAccount,
  loadDashboard,
  loadAccount,
  loadClients,
  loadClientNew,
  loadClientEdit,
  loadHub,
  loadActivities,
  loadInvoices,
  loadInvoiceNew,
  loadInvoiceDetail,
  loadLedger,
  loadJournalForm,
  loadLedgerAccountDetail,
  loadEmails,
  loadCalendar,
  loadOAuthCallback,
  loadConnectAuthorize,
  loadInvite,
  loadMemberOnboarding,
  loadNotFound,
  loadHelp,
  loadHelpArticle,
  loadReferral,
  loadBankAccounts,
  loadBankAccountDetail,
  loadBankAccountStatements,
  loadBankAccountStatementDetail,
  loadBankAccountUpload,
  loadClientStatement,
  loadMatterStatement,
  loadExpenses,
  loadExpenseNew,
  loadExpenseDetail,
  loadSupplierNew,
  loadSupplierEdit,
  loadSupplierDetail,
} from './lib/route-loaders';
import { lazyRoute } from './lib/lazy-route';

const Login = lazyRoute(loadLogin);
const Signup = lazyRoute(loadSignup);
const ForgotPassword = lazyRoute(loadForgotPassword);
const ResetPassword = lazyRoute(loadResetPassword);
const VerifyEmail = lazyRoute(loadVerifyEmail);
const VerifyEmailPending = lazyRoute(loadVerifyEmailPending);
const Unsubscribe = lazyRoute(loadUnsubscribe);
const Onboarding = lazyRoute(loadOnboarding);
const Activate = lazyRoute(loadActivate);
const SelectAccount = lazyRoute(loadSelectAccount);
const Dashboard = lazyRoute(loadDashboard);
const Account = lazyRoute(loadAccount);
const Clients = lazyRoute(loadClients);
const ClientNew = lazyRoute(loadClientNew);
const ClientEdit = lazyRoute(loadClientEdit);
const Hub = lazyRoute(loadHub);
const Activities = lazyRoute(loadActivities);
const Invoices = lazyRoute(loadInvoices);
const InvoiceNew = lazyRoute(loadInvoiceNew);
const InvoiceDetail = lazyRoute(loadInvoiceDetail);
const Ledger = lazyRoute(loadLedger);
const JournalForm = lazyRoute(loadJournalForm);
const LedgerAccountDetail = lazyRoute(loadLedgerAccountDetail);
const Emails = lazyRoute(loadEmails);
const Calendar = lazyRoute(loadCalendar);
const OAuthCallback = lazyRoute(loadOAuthCallback);
const ConnectAuthorize = lazyRoute(loadConnectAuthorize);
const Invite = lazyRoute(loadInvite);
const MemberOnboarding = lazyRoute(loadMemberOnboarding);
const NotFound = lazyRoute(loadNotFound);
const Help = lazyRoute(loadHelp);
const HelpArticle = lazyRoute(loadHelpArticle);
const Referral = lazyRoute(loadReferral);
const BankAccounts = lazyRoute(loadBankAccounts);
const BankAccountDetail = lazyRoute(loadBankAccountDetail);
const BankAccountStatements = lazyRoute(loadBankAccountStatements);
const BankAccountStatementDetail = lazyRoute(loadBankAccountStatementDetail);
const BankAccountUpload = lazyRoute(loadBankAccountUpload);
const ClientStatement = lazyRoute(loadClientStatement);
const MatterStatement = lazyRoute(loadMatterStatement);
const Expenses = lazyRoute(loadExpenses);
const ExpenseNew = lazyRoute(loadExpenseNew);
const ExpenseDetail = lazyRoute(loadExpenseDetail);
const SupplierNew = lazyRoute(loadSupplierNew);
const SupplierEdit = lazyRoute(loadSupplierEdit);
const SupplierDetail = lazyRoute(loadSupplierDetail);

// Dev-only live style-guide route: renders the real components from
// components/ui/ so the catalogue cannot drift the way a hand-written
// preview page can. The import is gated so the module is tree-shaken out of
// the prod bundle entirely.
const StyleGuide = import.meta.env.DEV ? lazy(() => import('./routes/styleguide')) : null;

/**
 * QueryClient is created inside QueryLayer so it can access the toast context.
 * This lets us auto-toast mutation errors globally without prop-drilling.
 */
function createQueryClient(addToast: ReturnType<typeof useToast>['addToast']) {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => {
          if (error && 'retryable' in error && !error.retryable) return false;
          return failureCount < 2;
        },
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        const key = JSON.stringify(query.queryKey);
        const detail = error instanceof ApiError ? `${error.status} ${error.code} - ${error.message}` : String(error);
        console.error(`[query] ${key} failed: ${detail}`, ...(error instanceof ApiError && error.details?.length ? ['\n' + error.details.map((d) => `  ${d.field}: ${d.message}`).join('\n')] : []));
        // 401s are BAU - JWTs expire on a fixed clock, and bumped token versions
        // (logout from another device) look the same to the client. Other 4xx are
        // deliberate server refusals the user is already told about; see
        // shouldReportToSentry.
        if (shouldReportToSentry(error)) {
          Sentry.captureException(error, { tags: { source: 'query' } });
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const detail = error instanceof ApiError ? `${error.status} ${error.code} - ${error.message}` : String(error);
        console.error(`[mutation] ${mutation.options.mutationKey ? JSON.stringify(mutation.options.mutationKey) : '(anonymous)'} failed: ${detail}`, ...(error instanceof ApiError && error.details?.length ? ['\n' + error.details.map((d) => `  ${d.field}: ${d.message}`).join('\n')] : []));
        if (shouldReportToSentry(error)) {
          Sentry.captureException(error, { tags: { source: 'mutation' } });
        }
        // Skip toast if the mutation has its own onError - component handles it locally
        if (mutation.options.onError) return;
        if (isRequestValidationError(error)) {
          // A malformed request from an out-of-date tab is the tab's fault, not the input's.
          void appVersion.check({ force: true }).then((stale) => {
            const toast = stale ? staleBundleToast() : toErrorToast(error);
            if (toast) addToast(toast);
          });
          return;
        }
        const toast = toErrorToast(error);
        if (toast) addToast(toast);
      },
    }),
  });
}

function LoadingFallback() {
  return <LoadingState message="Loading..." className="h-dvh" />;
}

// Orphaned route redirect: /clients/:clientId/trust-statement folded into
// the "Trust" pill on /clients/:clientId/statement. Preserve the incoming
// search params (e.g. a bookmarked from/to period) and force the Trust pill
// open, since the whole point of this old route was the trust view.
function TrustStatementRedirect() {
  const { clientId } = useParams<{ clientId: string }>();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('view', 'trust');
  return <Navigate to={`/clients/${clientId}/statement?${params.toString()}`} replace />;
}

/**
 * The routes inside the authenticated layout. Exported so `UiUsageRecorder` matches page views
 * against the real route table and a test can hold it, with `wizardRoutes`, to `BILL_ROUTE_TEMPLATES`.
 */
export const authenticatedRoutes = (
  <>
    <Route path="/" element={<Navigate to="/dashboard" replace />} />
    <Route path="/dashboard" element={<Dashboard />} />
    <Route path="/clients" element={<Clients />} />
    <Route path="/clients/new" element={<ClientNew />} />
    <Route path="/clients/:clientId/edit" element={<ClientEdit />} />
    <Route path="/clients/:clientId/statement" element={<ClientStatement />} />
    {/* Orphaned route: the combined statement page absorbed the trust
        statement into a "Trust" pill on /statement. Nothing links here
        any more, but an old bookmark or shared link should still land safely. */}
    <Route path="/clients/:clientId/trust-statement" element={<TrustStatementRedirect />} />
    <Route path="/clients/:clientId" element={<Hub />} />
    <Route path="/matters/:matterId" element={<Hub />} />
    <Route path="/matters/:matterId/statement" element={<MatterStatement />} />
    <Route path="/activities" element={<Activities />} />
    <Route path="/invoices" element={<Invoices />} />
    <Route path="/invoices/new" element={<InvoiceNew />} />
    <Route path="/invoices/:invoiceId" element={<InvoiceDetail />} />
    <Route path="/accounting" element={<Ledger />} />
    <Route path="/accounting/journal-form" element={<JournalForm />} />
    <Route path="/accounting/accounts/:code" element={<LedgerAccountDetail />} />
    <Route path="/expenses" element={<Expenses />} />
    <Route path="/expenses/new" element={<ExpenseNew />} />
    <Route path="/expenses/:expenseId" element={<ExpenseDetail />} />
    {/* Suppliers are listed on the Expenses page but get their own
        top-level URLs, so a supplier is linkable from an expense, a bank
        line, or a shared link - the same reason /matters/:matterId sits
        outside /clients. Order matters: /new before /:supplierId. */}
    <Route path="/suppliers/new" element={<SupplierNew />} />
    <Route path="/suppliers/:supplierId/edit" element={<SupplierEdit />} />
    <Route path="/suppliers/:supplierId" element={<SupplierDetail />} />
    <Route path="/emails" element={<Emails />} />
    <Route path="/calendar" element={<Calendar />} />
    <Route path="/settings" element={<Account />} />
    <Route path="/help" element={<Help />} />
    <Route path="/help/:slug" element={<HelpArticle />} />
    <Route path="/referral" element={<Referral />} />
    <Route path="/bank-accounts" element={<BankAccounts />} />
    <Route path="/bank-accounts/:bankAccountId" element={<BankAccountDetail />} />
    <Route path="/bank-accounts/:bankAccountId/statements" element={<BankAccountStatements />} />
    <Route
      path="/bank-accounts/:bankAccountId/statements/:statementId"
      element={<BankAccountStatementDetail />}
    />
    <Route path="/bank-accounts/:bankAccountId/upload" element={<BankAccountUpload />} />
    <Route path="/subscribe" element={<Navigate to="/settings?tab=subscription" replace />} />
    <Route path="*" element={<NotFound />} />
  </>
);

/**
 * The setup wizard an invited member completes after accepting. Exported
 * for the same reason as `authenticatedRoutes`.
 */
export const wizardRoutes = (
  <>
    <Route path="/member-onboarding" element={<MemberOnboarding />} />
  </>
);

/**
 * AppRoutes handles all routing logic with proper auth guards.
 * Wrapped in a separate component so it can access AuthContext.
 */
function AppRoutes() {
  const { isInitializing } = useAuth();
  useReloadOnNavigationWhenStale();

  // Global loading screen during auth initialization
  // This prevents ANY route from rendering until we know auth state
  if (isInitializing) {
    return <LoadingFallback />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Protected routes - require authentication AND account */}
        <Route
          element={
            <RouteGuard requireAccount>
              <AccountProvider>
                <SubscriptionProvider>
                  <Layout routes={authenticatedRoutes} />
                </SubscriptionProvider>
              </AccountProvider>
            </RouteGuard>
          }
        >
          {authenticatedRoutes}
        </Route>

        {/* Onboarding - authenticated but no account required */}
        <Route
          path="/onboarding"
          element={
            <RouteGuard>
              <RouteErrorBoundary>
                <Onboarding />
              </RouteErrorBoundary>
            </RouteGuard>
          }
        />

        {/* Subscription activation - authenticated, shown after Paystack checkout callback */}
        <Route
          path="/activate"
          element={
            <RouteGuard>
              <Activate />
            </RouteGuard>
          }
        />

        {/* Select Account - authenticated but no account required */}
        <Route
          path="/select-account"
          element={
            <RouteGuard>
              <RouteErrorBoundary>
                <SelectAccount />
              </RouteErrorBoundary>
            </RouteGuard>
          }
        />

        {/* The member setup wizard - authenticated, account context available (for dialect-aware
            copy and the member's role) but `requireAccount` is deliberately omitted: a
            wizard must render the moment the user has an accountId from auth, not wait
            on a fully-loaded account. */}
        <Route
          element={
            <RouteGuard>
              <RouteErrorBoundary>
                <AccountProvider>
                  <WizardShell routes={wizardRoutes} />
                </AccountProvider>
              </RouteErrorBoundary>
            </RouteGuard>
          }
        >
          {wizardRoutes}
        </Route>

        {/* Guest-only routes - redirect authenticated users */}
        <Route
          path="/login"
          element={
            <RouteGuard requireGuest>
              <Login />
            </RouteGuard>
          }
        />
        <Route
          path="/signup"
          element={
            <RouteGuard requireGuest>
              <Signup />
            </RouteGuard>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <RouteGuard requireGuest>
              <ForgotPassword />
            </RouteGuard>
          }
        />
        {/* No RouteGuard: both authenticated and unauthenticated users may
            land here. The setup flow (logged-in user adding email+password as a
            sign-in method) lands here while authenticated; the reset flow
            (forgot-password) lands here unauthenticated. The page operates
            purely on the URL token, not on JWT auth state. After success the
            backend bumps tokenVersion, invalidating the current JWT, and the
            page navigates to /login - a logged-in user is logged out at that
            moment, matching standard "password change ends all sessions"
            behaviour. */}
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* No RouteGuard: both authenticated and unauthenticated users may click
            the verification link from their inbox. */}
        <Route path="/auth/verify-email" element={<VerifyEmail />} />
        {/* No RouteGuard: the unsubscribe link in a notification email must
            work signed in or out; the token in the URL is the credential. */}
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        {/* requireGuest: the user is unauthenticated at this point – register
            no longer mints a JWT. The page reads the email from sessionStorage
            stashed by signup.tsx immediately before navigating here. */}
        <Route
          path="/auth/verify-email-pending"
          element={
            <RouteGuard requireGuest>
              <VerifyEmailPending />
            </RouteGuard>
          }
        />
        <Route path="/register" element={<Navigate to="/signup" replace />} />

        {/* OAuth callback - no auth guard, handles its own state */}
        <Route path="/oauth/callback" element={<OAuthCallback />} />

        {/* Where an assistant sends a user to approve a connection. It chooses its
            own account, so the session needs none. */}
        <Route
          path="/connect/authorize"
          element={
            <RouteGuard>
              <ConnectAuthorize />
            </RouteGuard>
          }
        />

        {/* Dev-only live style guide: renders the real components/ui/
            primitives so the catalogue tracks the shipped shapes rather than
            a hand-written description of them. Tree-shaken from the prod
            bundle by the conditional dynamic import above. Wrapped in
            RouteGuard (auth required, no account required) as
            defence-in-depth: if a developer ever runs the dev server on a
            shared network, the page stays behind login. */}
        {StyleGuide && (
          <Route
            path="/styleguide"
            element={
              <RouteGuard>
                <StyleGuide />
              </RouteGuard>
            }
          />
        )}

        {/* Invite acceptance - no auth guard, handles both authenticated and unauthenticated states */}
        <Route path="/invite/:token" element={<Invite />} />

        {/* Fallback - matches any unrecognised URL outside authenticated layout */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Bridges the toast system into React Query so mutation errors auto-toast.
 * Must be rendered inside ToastProvider.
 */
function QueryLayer({ children }: { children: React.ReactNode }) {
  const { addToast } = useToast();
  // Stable ref - queryClient is created once per mount
  const [queryClient] = useState(() => createQueryClient(addToast));
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <QueryLayer>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </QueryLayer>
      </ToastProvider>
    </ErrorBoundary>
  );
}
